import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/utils/axiosInterceptor";
import type {
	MonthEndStock,
	MonthEndStockWithItems,
	RecalculateMonthEndStockRequest,
	RecalculateMonthEndStockResponse,
	LockMonthEndPeriodRequest,
	UnlockMonthEndPeriodRequest,
} from "@/types/monthEndStock";

export const useMonthEndStock = () => {
	const queryClient = useQueryClient();

	// Get all month-end stock periods
	const {
		data: periods,
		isLoading: isLoadingPeriods,
		error: periodsError,
	} = useQuery({
		queryKey: ["monthEndStock"],
		queryFn: async () => {
			const response = await api.get<{
				status: string;
				data: MonthEndStock[];
			}>("/month-end-stock");
			return response.data.data;
		},
	});

	// Get locked periods
	const {
		data: lockedPeriods,
		isLoading: isLoadingLockedPeriods,
		error: lockedPeriodsError,
	} = useQuery({
		queryKey: ["monthEndStock", "locked"],
		queryFn: async () => {
			const response = await api.get<{
				status: string;
				data: MonthEndStock[];
			}>("/month-end-stock/locked");
			return response.data.data;
		},
	});

	// Get a single period by ID
	const usePeriodById = (periodId: string | undefined) => {
		return useQuery({
			queryKey: ["monthEndStock", periodId],
			queryFn: async () => {
				const response = await api.get<{
					status: string;
					data: MonthEndStockWithItems;
				}>(`/month-end-stock/${periodId}`);
				return response.data.data;
			},
			enabled: !!periodId,
		});
	};

	// Recalculate month-end stock
	const recalculateMutation = useMutation({
		mutationFn: (data: RecalculateMonthEndStockRequest) =>
			api.post<{
				status: string;
				data: RecalculateMonthEndStockResponse;
			}>("/month-end-stock/recalculate", data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["monthEndStock"] });
		},
	});

	// Apply values
	const applyValuesMutation = useMutation({
		mutationFn: (periodId: string) =>
			api.post<{
				status: string;
				data: {
					success: boolean;
					itemsUpdated: number;
					periodId: string;
					voucherNumber: string;
				};
			}>(`/month-end-stock/${periodId}/apply-values`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["monthEndStock"] });
		},
	});

	// Lock period
	const lockPeriodMutation = useMutation({
		mutationFn: (data: LockMonthEndPeriodRequest) =>
			api.post<{ status: string; data: MonthEndStock }>(
				"/month-end-stock/lock",
				data,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["monthEndStock"] });
		},
	});

	// Unlock period
	const unlockPeriodMutation = useMutation({
		mutationFn: (data: UnlockMonthEndPeriodRequest) =>
			api.post<{ status: string; data: MonthEndStock }>(
				"/month-end-stock/unlock",
				data,
			),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["monthEndStock"] });
		},
	});

	// Delete period
	const deletePeriodMutation = useMutation({
		mutationFn: (periodId: string) =>
			api.delete(`/month-end-stock/${periodId}`),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["monthEndStock"] });
		},
	});

	return {
		// Data
		periods,
		lockedPeriods,
		isLoadingPeriods,
		isLoadingLockedPeriods,
		periodsError,
		lockedPeriodsError,

		// Queries
		usePeriodById,

		// Mutations
		recalculate: recalculateMutation.mutateAsync,
		isRecalculating: recalculateMutation.isPending,
		recalculateError: recalculateMutation.error,

		applyValues: applyValuesMutation.mutateAsync,
		isApplyingValues: applyValuesMutation.isPending,
		applyValuesError: applyValuesMutation.error,

		lockPeriod: lockPeriodMutation.mutateAsync,
		isLocking: lockPeriodMutation.isPending,
		lockError: lockPeriodMutation.error,

		unlockPeriod: unlockPeriodMutation.mutateAsync,
		isUnlocking: unlockPeriodMutation.isPending,
		unlockError: unlockPeriodMutation.error,

		deletePeriod: deletePeriodMutation.mutateAsync,
		isDeleting: deletePeriodMutation.isPending,
		deleteError: deletePeriodMutation.error,
	};
};
